import { useNostr } from '@nostrify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';

// Kind 7 reactions for skills
// Content "+" = vouch/approve, "-" = flag/disapprove

export interface SkillReview {
  id: string;
  pubkey: string;
  content: string; // "+" or "-"
  createdAt: number;
}

export interface SkillTrustScore {
  vouches: number;
  flags: number;
  totalReviews: number;
  score: number; // vouches - flags
  userVouch: '+' | '-' | null; // current user's vote
}

export function useSkillReviews(skillEventId: string) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery({
    queryKey: ['skill-reviews', skillEventId],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);

      // Query reactions (kind 7) for this skill event
      const events = await nostr.query([{
        kinds: [7],
        '#e': [skillEventId],
        limit: 500,
      }], { signal });

      // Parse reactions
      const reviews: SkillReview[] = events
        .filter((e: NostrEvent) => e.content === '+' || e.content === '-')
        .map((e: NostrEvent) => ({
          id: e.id,
          pubkey: e.pubkey,
          content: e.content,
          createdAt: e.created_at,
        }));

      // Calculate trust score
      const vouches = reviews.filter(r => r.content === '+').length;
      const flags = reviews.filter(r => r.content === '-').length;
      
      // Get user's vote if logged in
      let userVouch: '+' | '-' | null = null;
      if (user) {
        const userReview = reviews.find(r => r.pubkey === user.pubkey);
        if (userReview) {
          userVouch = userReview.content as '+' | '-';
        }
      }

      const trustScore: SkillTrustScore = {
        vouches,
        flags,
        totalReviews: reviews.length,
        score: vouches - flags,
        userVouch,
      };

      return {
        reviews,
        trustScore,
      };
    },
    enabled: !!skillEventId,
  });
}

export function useVouchSkill() {
  const { mutateAsync: createEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ skillEventId, skillPubkey, vote }: { 
      skillEventId: string; 
      skillPubkey: string;
      vote: '+' | '-';
    }) => {
      await createEvent({
        kind: 7,
        content: vote,
        tags: [
          ['e', skillEventId],
          ['p', skillPubkey],
        ],
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate the reviews cache
      queryClient.invalidateQueries({ queryKey: ['skill-reviews', variables.skillEventId] });
    },
  });
}
